// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import { CircularProgress } from '@mui/material'
import Button from '@mui/material/Button/Button'
import classNames from 'classnames'
import { find, isEqual, toInteger } from 'lodash'
import moment from 'moment'
import React from 'react'
import { Mutation, MutationFunction } from 'react-apollo'
import ErrorMessage from '../../common/error/ErrorMessage'
import { removeTypename } from '../../common/utils'
import type { Setting } from '../admin/station/stationSettings/StationSettingsPage'
import { i18n } from '../index'
import type { Station } from '../station/stationTypes'
import withActiveStation from '../station/withActiveStation'
import { upsertCustomerTargetsMutation } from './customerTargetQueries'
import css from './customerTargetTable.css'
import type { CustomerTargetTableData, DailySales, GroupedCustomerTargets } from './customerTargetTypes'
import { getProductGroupLabel, getProductGroupName, getTableData } from './helpers'

type Props = {
  canEdit: boolean
  isSavingCustomerTarget: boolean
  setIsSavingCustomerTarget: (value: boolean) => void
  saveCustomerTargetErrorMessage: string | undefined
  setSaveCustomerTargetErrorMessage: (value: string | undefined) => void
  week: string
  productGroup: string
  onProductGroupChange: (...args: Array<any>) => any
  customerTargets: GroupedCustomerTargets
  dailySales: DailySales[]
  activeStation: Station
  stationSettings: Setting[]
}
type State = {
  data: CustomerTargetTableData[]
  salesNotes: { date: string; notes: string }[]
}
const TODAY = moment()
const requiresStationSetting = ['ABCBurger', 'ABCPizza', 'AbcPizzaTable', 'Breakfast', 'Arnolds', 'AbcBuffetWorld']

@withActiveStation
class CustomerTargetTable extends React.Component<Props, State> {
  state = {
    data: getTableData(this.props.customerTargets, this.props.dailySales, this.props.week),
    salesNotes: removeTypename(this.props.salesDetails),
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { customerTargets, dailySales, week } = this.props

    if (
      week !== prevProps.week ||
      (customerTargets && !isEqual(customerTargets, prevProps.customerTargets)) ||
      (dailySales && !isEqual(dailySales, prevProps.dailySales))
    ) {
      this.setState({
        data: getTableData(customerTargets, dailySales, week),
      })
    }
  }

  onSubmit = async (mutation: MutationFunction) => {
    const { data, salesNotes } = this.state
    const { week, activeStation, setIsSavingCustomerTarget, setSaveCustomerTargetErrorMessage } = this.props
    setIsSavingCustomerTarget(true)
    setSaveCustomerTargetErrorMessage(undefined)
    const items = []
    // GroupBuffet and GroupDining data should not be updated through api, its data directly comes from group reservations
    data
      .filter(
        (d) =>
          d.productGroup !== 'GroupBuffet' &&
          d.productGroup !== 'GroupDining' &&
          d.productGroup !== 'GroupPizzaTable' &&
          d.productGroup !== 'GroupBuffetWorld',
      )
      .forEach((productGroupData) => {
        const date = moment(week).startOf('week')
        productGroupData.data.forEach((cell, cellIndex) => {
          if (cellIndex > 0) {
            date.add(1, 'days')
          }

          const cellEditable = date.clone().add(1, 'days').isSameOrAfter(TODAY)
          const value = cell.value || (cellEditable && cell.calculatedValue)

          if (value) {
            items.push({
              date: date.format('YYYY-MM-DD'),
              productType: productGroupData.productGroup,
              target: Math.round(Number(value)),
            })
          }
        })
      })
    await mutation({
      variables: {
        stationId: activeStation.id,
        customerTargets: items,
        dailySalesDetails: salesNotes,
      },
      onError: (err) => {
        setSaveCustomerTargetErrorMessage(i18n.t('customerTarget.saveFailed', { week: moment(week).format('W') }))
      },
    })
    setIsSavingCustomerTarget(false)
  }
  setCellValue = (newValue: string, rowIndex: number, cellIndex: number) => {
    const { data } = this.state
    let value = newValue

    if (value !== '') {
      if (!Number(newValue)) {
        value = ''
      } else {
        value = toInteger(newValue)

        if (value < 1) {
          value = ''
        }
      }
    }

    data[rowIndex].data[cellIndex].value = String(value)
    this.setState({
      data,
    })
  }
  setNotesCellValue = (newValue: string, rowIndex: number) => {
    const { salesNotes } = this.state
    salesNotes[rowIndex].notes = newValue || ''
    this.setState({
      salesNotes,
    })
  }

  render() {
    const {
      canEdit,
      isSavingCustomerTarget,
      onProductGroupChange,
      week,
      productGroup,
      stationSettings,
      saveCustomerTargetErrorMessage,
    } = this.props
    const { data } = this.state
    return (
      <Mutation mutation={upsertCustomerTargetsMutation}>
        {(mutation) => (
          <div className={css.component}>
            {data.map((row, i) => {
              const date = moment(week).startOf('week')

              if (requiresStationSetting.includes(row.productGroup)) {
                const settingIsOn = find(stationSettings, {
                  name: getProductGroupName(row.productGroup),
                  category: 'customerTarget',
                })
                if (!settingIsOn) {
                  return null
                }
              }

              return (
                <div
                  key={i}
                  className={classNames(css.row, {
                    [css.rowActive]: row.productGroup === productGroup,
                  })}
                  onClick={() => onProductGroupChange(row.productGroup)}
                >
                  <div className={css.label} key={i}>
                    <a className={css.labelLink} onClick={() => onProductGroupChange(row.productGroup)}>
                      {getProductGroupLabel(row.productGroup)}
                    </a>
                  </div>
                  {row.data.map((cell, y) => {
                    if (y > 0) {
                      date.add(1, 'days')
                    }

                    // cell is editable if the date is not in the past and page is in edit mode
                    const cellEditable =
                      moment(date.format('YYYY-MM-DD')).isSameOrAfter(TODAY.format('YYYY-MM-DD')) &&
                      'GroupBuffet' !== row.productGroup &&
                      'GroupDining' !== row.productGroup &&
                      'GroupPizzaTable' !== row.productGroup &&
                      'GroupBuffetWorld' !== row.productGroup
                    const isToday = TODAY.format('YYYY-MM-DD') === date.format('YYYY-MM-DD')
                    const textFieldProps: {
                      value: string
                      placeholder?: string
                    } = {
                      value: cell.value,
                      placeholder: cell.calculatedValue ? String(Math.ceil(Number(cell.calculatedValue))) : '0',
                    }
                    return (
                      <div
                        key={y}
                        className={classNames(css.column, {
                          [css.columnToday]: isToday,
                        })}
                      >
                        {canEdit && cellEditable ? (
                          <input
                            {...textFieldProps}
                            type='number'
                            className={css.field}
                            onChange={(e) => this.setCellValue(e.target.value, i, y)}
                          />
                        ) : (
                          <span
                            className={classNames({
                              [css.calculatedValue]: !cell.value,
                            })}
                          >
                            {cell.value ? cell.value : cell.calculatedValue}
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}

            <div className={classNames(css.row)}>
              <div className={css.label}>
                <a className={css.labelLink}>{i18n.t('customerTarget.notesLabel')}</a>
              </div>
              {this.state.salesNotes.map(({ notes, date: stringDate }, i) => {
                const date = moment(stringDate)
                return (
                  <div
                    key={i}
                    className={classNames(css.column, css.text, {
                      [css.columnToday]: TODAY.isSame(date, 'day'),
                    })}
                  >
                    {canEdit ? (
                      <textarea
                        value={notes}
                        className={classNames(css.field, css.text)}
                        onChange={(e) => this.setNotesCellValue(e.target.value, i)}
                      />
                    ) : (
                      <span className={classNames(css.text)}>{notes}</span>
                    )}
                  </div>
                )
              })}
            </div>

            <div className={css.actions}>
              {saveCustomerTargetErrorMessage && (
                <div data-testid='customer-target-table-error-message' className={css.errorMessage}>
                  <ErrorMessage message={saveCustomerTargetErrorMessage} />
                </div>
              )}
              {canEdit && (
                <div>
                  <Button
                    data-testid='customer-target-table-save-button'
                    className={css.saveButton}
                    variant='contained'
                    color='primary'
                    disabled={isSavingCustomerTarget}
                    onClick={() => this.onSubmit(mutation)}
                  >
                    {i18n.t('customerTarget.save')}
                    {isSavingCustomerTarget && <CircularProgress className={css.saveButtonProgress} size={20} />}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Mutation>
    )
  }
}

export default CustomerTargetTable
